import { ChatViewMode } from 'enums/ChatViewMode';
import { OverlayType } from 'enums/OverlayType';
import { TabView } from 'enums/TabView';
import { ContextMenuData } from 'interfaces/ContextMenuData';
import { DialogAggregateInfo } from 'interfaces/DialogAggregateInfo';
import { DrawerData } from 'interfaces/DrawerData';
import { HoveringButtonInfo } from 'interfaces/HoveringButtonInfo';
import { ImageDisplayData, ImagesDisplayData } from 'interfaces/ImageData';
import { MessageData } from 'interfaces/MessageData';
import { OverlayProps } from 'interfaces/OverlayProps';
import { QuickActionButtonData } from 'interfaces/QuickActionButtonData';
import { QuoteData } from 'interfaces/QuoteData';
import { SmartButtonAction } from 'interfaces/SmartButtonAction';
import { Toast } from 'interfaces/Toast';
import { randomStr, randomUuid } from 'utils/random';

export const enum ChatActionType {
    SetOpen = 'CHAT/SET_OPEN',
    SetBlurDelay = 'CHAT/SET_BLUR_DELAY',
    Toggle = 'CHAT/TOGGLE',
    FullScreen = 'CHAT/FULLSCREEN',
    SetActiveTab = 'CHAT/SET_ACTIVE_TAB',

    SetViewMode = 'CHAT/SET_VIEW_MODE',
    SetOverlay = 'CHAT/SET_OVERLAY',
    SetDrawer = 'CHAT/SET_DRAWER',

    SendTyping = 'CHAT/SEND_TYPING',
    ReceiveAgentTyping = 'CHAT/RECEIVE_AGENT_TYPING',
    SetAgentTyping = 'CHAT/SET_AGENT_TYPING',
    SetWaitingForBotReply = 'CHAT/SET_WAITING_FOR_BOT_REPLY',

    SetUnreadCounter = 'CHAT/SET_UNREAD_COUNTER',
    SavePreAuthUnreadCounter = 'CHAT/SAVE_PRE_AUTH_UNREAD_COUNTER',
    ResetPreAuthUnreadCounter = 'CHAT/RESET_PRE_AUTH_UNREAD_COUNTER',

    SetShowMsgTimeAndStatus = 'CHAT/SET_MSG_TIME_AND_STATUS',
    SetQuotedMessage = 'CHAT/SET_QUOTED_MESSAGE',
    SetHoveringButtons = 'CHAT/SET_HOVERING_BUTTONS',
    SetIsHoveringButtonsVisible = 'CHAT/SET_IS_HOVERING_BUTTONS_VISIBLE',
    SetQuickActionsMenu = 'CHAT/SET_QUICK_ACTIONS_MENU',
    SetQuickActionsMenuReducer = 'CHAT/SET_QUICK_ACTIONS_MENU_REDUCER',
    SetQuickActionsMenuVisible = 'CHAT/SET_QUICK_ACTIONS_MENU_VISIBLE',

    BlinkMessage = 'CHAT/BLINK_MESSAGE',
    SmartButtonClick = 'CHAT/SMART_BUTTON_CLICK',
    OpenMediaGallery = 'CHAT/OPEN_MEDIA_GALLERY',

    ViewImage = 'CHAT/VIEW_IMAGE',
    ViewImageList = 'CHAT/VIEW_IMAGES',
    CloseImage = 'CHAT/CLOSE_IMAGE',

    SetSearchQuery = 'CHAT/SET_SEARCH_QUERY',
    SearchRequest = 'CHAT/SEARCH_REQUEST',
    SetSearchResults = 'CHAT/SET_SEARCH_RESULTS',
    CancelSearch = 'CHAT/CANCEL_SEARCH',
    GoToSearchResult = 'CHAT/GO_TO_SEARCH_RESULT',

    OpenContextMenu = 'CHAT/OPEN_CONTEXT_MENU',
    UpdateContextMenu = 'CHAT/UPDATE_CONTEXT_MENU',
    CloseContextMenu = 'CHAT/CLOSE_CONTEXT_MENU',

    SetNotificationsEnabled = 'CHAT/SET_NOTIFICATIONS_ENABLED',
    SetSoundNotificationsEnabled = 'CHAT/SET_SOUND_NOTIFICATIONS_ENABLED',
    SetVibrationEnabled = 'CHAT/SET_VIBRATION_ENABLED',
    SetMaximized = 'CHAT/SET_MAXIMIZED',

    CurrentHistoryHtmlRequest = 'CHAT/CURRENT_HISTORY_HTML_REQUEST',
    CurrentHistoryHtmlResponse = 'CHAT/CURRENT_HISTORY_HTML_RESPONSE',
    SetBackgroundOperation = 'CHAT/SET_BACKGROUND_OPERATION',

    SetMenuActive = 'CHAT/SET_MENU_ACTIVE',
    SetSwipedMessage = 'CHAT/SET_SWIPED_MESSAGE',

    AmendLastAsrResult = 'CHAT/AMEND_LAST_ASR_RESULT',
    CommitLastAsrResult = 'CHAT/COMMIT_LAST_ASR_RESULT',
    RollbackLastAsrResult = 'CHAT/ROLLBACK_LAST_ASR_RESULT',

    ShowToast = 'CHAT/SHOW_TOAST',

    SetIsScrolling = 'CHAT/SET_IS_SCROLLING',
}

export type SetChatOpenAction = ReturnType<typeof setChatOpen>;
export const setChatOpen = (open: boolean) => ({
    type: ChatActionType.SetOpen,
    payload: open,
});

export type ToggleChatAction = ReturnType<typeof toggleChat>;
export const toggleChat = () => ({
    type: ChatActionType.Toggle,
});

export type SetChatFullScreenAction = ReturnType<typeof setChatFullScreen>;
export const setChatFullScreen = (fullscreen: boolean) => ({
    type: ChatActionType.FullScreen,
    payload: fullscreen,
});

export type SetActiveTabAction = ReturnType<typeof setActiveTab>;
export const setActiveTab = (tab: TabView) => ({
    type: ChatActionType.SetActiveTab,
    payload: tab,
});

export type SetChatOverlayAction = ReturnType<typeof setChatOverlay>;
export const setChatOverlay = <TOverlayType extends OverlayType>(
    type: TOverlayType,
    blurChat = true,
    props?: OverlayProps<TOverlayType>,
) => ({
    type: ChatActionType.SetOverlay,
    payload: { type, blurChat, props },
});

export const closeChatOverlay: () => SetChatOverlayAction = () => ({
    type: ChatActionType.SetOverlay,
    payload: null,
});

export type SendTypingAction = ReturnType<typeof sendChatTyping>;
export const sendChatTyping = () => ({
    type: ChatActionType.SendTyping,
});

export type ReceiveAgentTypingAction = ReturnType<typeof receiveAgentTyping>;
export const receiveAgentTyping = () => ({
    type: ChatActionType.ReceiveAgentTyping,
});

export type SetAgentTypingAction = ReturnType<typeof setAgentTyping>;
export const setAgentTyping = (typing: boolean) => ({
    type: ChatActionType.SetAgentTyping,
    payload: typing,
});

export type SetUnreadCounterAction = ReturnType<typeof setUnreadCounter>;
export const setUnreadCounter = (count: number) => ({
    type: ChatActionType.SetUnreadCounter,
    payload: count,
});

export type ViewImageAction = ReturnType<typeof viewImage>;
export const viewImage = <T = Record<string, unknown>>(args: ImageDisplayData<T>) => ({
    type: ChatActionType.ViewImage,
    payload: (args.src ? args : null) as ImageDisplayData,
});

export type ViewImageListAction = ReturnType<typeof viewImageList>;
export const viewImageList = ({ currentIndex = 0, ...rest }: ImagesDisplayData) => ({
    type: ChatActionType.ViewImageList,
    payload: { currentIndex, ...rest } as ImagesDisplayData,
});

export type CloseImageAction = ReturnType<typeof closeImage>;
export const closeImage = () => ({
    type: ChatActionType.CloseImage,
});

export type SetChatViewModeAction = ReturnType<typeof setChatViewMode>;
export const setChatViewMode = (viewMode: ChatViewMode, goToMsgId?: string) => ({
    type: ChatActionType.SetViewMode,
    payload: { viewMode, goToMsgId },
});

export type SetSearchQueryAction = ReturnType<typeof setSearchQuery>;
export const setSearchQuery = (query: string) => ({
    type: ChatActionType.SetSearchQuery,
    payload: query,
});

export type SearchRequestAction = ReturnType<typeof searchRequest>;
export const searchRequest = ({
    query,
    aborter = null,
}: {
    query: string;
    aborter?: AbortSignal;
}) => ({
    type: ChatActionType.SearchRequest,
    payload: { query, aborter },
});

export type SetSearchResultsAction = ReturnType<typeof setSearchResults>;
export const setSearchResults = (msgIds: string[], words: string[] = null) => ({
    type: ChatActionType.SetSearchResults,
    payload: { msgIds, words },
});

export type CancelSearchAction = ReturnType<typeof cancelSearch>;
export const cancelSearch = () => ({
    type: ChatActionType.CancelSearch,
});

export type GoToSearchResultAction = ReturnType<typeof goToSearchResult>;
export const goToSearchResult = (resultIdx: number) => ({
    type: ChatActionType.GoToSearchResult,
    payload: resultIdx,
});

export type SetHoveringButtonsAction = ReturnType<typeof setHoveringButtons>;
export const setHoveringButtons = (buttons?: HoveringButtonInfo[]) => ({
    type: ChatActionType.SetHoveringButtons,
    payload: buttons,
});

export type SetIsHoveringButtonsVisibleAction = ReturnType<typeof setIsHoveringButtonsVisible>;
export const setIsHoveringButtonsVisible = (flag: boolean) => ({
    type: ChatActionType.SetIsHoveringButtonsVisible,
    payload: flag,
});

export type ContextMenuAction =
    | ReturnType<typeof openContextMenu>
    | ReturnType<typeof updateContextMenu>;

export const openContextMenu = (options: ContextMenuData) => ({
    type: ChatActionType.OpenContextMenu,
    payload: options,
});

export type DrawerAction = ReturnType<typeof setDrawer>;
export const setDrawer = (options: DrawerData) => ({
    type: ChatActionType.SetDrawer,
    payload: options,
});

export const updateContextMenu = (options: ContextMenuData) => ({
    type: ChatActionType.UpdateContextMenu,
    payload: options,
});

export type CloseContextMenuAction = ReturnType<typeof closeContextMenu>;
export const closeContextMenu = () => ({
    type: ChatActionType.CloseContextMenu,
});

export type SetQuotedMessageAction = ReturnType<typeof setQuotedMessage>;
export const setQuotedMessage = (quoteData: QuoteData) => ({
    type: ChatActionType.SetQuotedMessage,
    payload: quoteData,
});

export type BlinkMessageAction = ReturnType<typeof blinkMessage>;
export const blinkMessage = (msgId?: string) => ({
    type: ChatActionType.BlinkMessage,
    payload: msgId ? { msgId, uuid: randomStr() } : null,
});

export type SetNotificationsEnabledAction = ReturnType<typeof setNotificationsEnabled>;
export const setNotificationsEnabled = (enable: boolean) => ({
    type: ChatActionType.SetNotificationsEnabled,
    payload: enable,
});

export type SetSoundNotificationsEnabledAction = ReturnType<typeof setSoundNotificationsEnabled>;
export const setSoundNotificationsEnabled = (enable: boolean) => ({
    type: ChatActionType.SetSoundNotificationsEnabled,
    payload: enable,
});

export type SetShowMsgTimeAndStatusAction = ReturnType<typeof setShowMsgTimeAndStatus>;
export const setShowMsgTimeAndStatus = (show: boolean) => ({
    type: ChatActionType.SetShowMsgTimeAndStatus,
    payload: show,
});

export type SetVibrationEnabledAction = ReturnType<typeof setVibrationEnabled>;
export const setVibrationEnabled = (vibrate: boolean) => ({
    type: ChatActionType.SetVibrationEnabled,
    payload: vibrate,
});

export type SetChatMaximizedAction = ReturnType<typeof setChatMaximized>;
export const setChatMaximized = (maximized: boolean) => ({
    type: ChatActionType.SetMaximized,
    payload: maximized,
});

export type SmartButtonClickAction = ReturnType<typeof smartButtonClick>;
export const smartButtonClick = (buttonAction: SmartButtonAction, fallbackText?: string) => ({
    type: ChatActionType.SmartButtonClick,
    payload: { buttonAction, fallbackText },
});

export type MessagesHtmlRequestAction = ReturnType<typeof messagesHtmlRequest>;
export const messagesHtmlRequest = (messages: MessageData[]) => ({
    type: ChatActionType.CurrentHistoryHtmlRequest,
    payload: { requestId: randomUuid(), messages },
});

export type CurrentHistoryHtmlResponseAction = ReturnType<typeof messagesHtmlResponse>;
export const messagesHtmlResponse = (requestId: string, html: string, css: string) => ({
    type: ChatActionType.CurrentHistoryHtmlResponse,
    payload: { requestId, html, css },
});

export type SetBackgroundOperationAction = ReturnType<typeof setBackgroundOperation>;
export const setBackgroundOperation = (desc: string, progress?: number) => ({
    type: ChatActionType.SetBackgroundOperation,
    payload: { desc, progress },
});

export type SavePreAuthUnreadCounterAction = ReturnType<typeof savePreAuthUnreadCounter>;
export const savePreAuthUnreadCounter = () => ({
    type: ChatActionType.SavePreAuthUnreadCounter,
});

export type ResetPreAuthUnreadCounterAction = ReturnType<typeof resetPreAuthUnreadCounter>;
export const resetPreAuthUnreadCounter = () => ({
    type: ChatActionType.ResetPreAuthUnreadCounter,
});

export type SetQuickActionsMenuAction = ReturnType<typeof setQuickActionsMenu>;
export const setQuickActionsMenu = (buttons: QuickActionButtonData[]) => ({
    type: ChatActionType.SetQuickActionsMenu,
    payload: buttons,
});

export type SetQuickActionsMenuActionReducer = ReturnType<typeof SetQuickActionsMenuReducer>;
export const SetQuickActionsMenuReducer = (buttons: QuickActionButtonData[]) => ({
    type: ChatActionType.SetQuickActionsMenuReducer,
    payload: buttons,
});

export type SetQuickActionsMenuActionVisible = ReturnType<typeof setQuickActionsMenuVisible>;
export const setQuickActionsMenuVisible = (visible: boolean) => ({
    type: ChatActionType.SetQuickActionsMenuVisible,
    payload: visible,
});

export type SetWaitingForBotReplyAction = ReturnType<typeof setWaitingForBotReply>;
export const setWaitingForBotReply = (waiting: boolean) => ({
    type: ChatActionType.SetWaitingForBotReply,
    payload: waiting,
});

export type OpenMediaGalleryAction = ReturnType<typeof openMediaGallery>;
export const openMediaGallery = (mediaInfo: DialogAggregateInfo, focusMediaType?: string) => ({
    type: ChatActionType.OpenMediaGallery,
    payload: { mediaInfo, focusMediaType },
});

export type SetSwipedMessageAction = ReturnType<typeof setSwipedMessage>;
export const setSwipedMessage = (swipedMessage: MessageData) => ({
    type: ChatActionType.SetSwipedMessage,
    payload: swipedMessage,
});

export type ShowToastAction = ReturnType<typeof showToast>;
export const showToast = (payload: Toast) => ({
    type: ChatActionType.ShowToast,
    payload,
});

export type SetIsScrolling = ReturnType<typeof setIsScrolling>;
export const setIsScrolling = (payload: boolean) => ({
    type: ChatActionType.SetIsScrolling,
    payload,
});

export type SetBlurDelay = ReturnType<typeof setBlurDelay>;
export const setBlurDelay = (payload: number) => ({
    type: ChatActionType.SetBlurDelay,
    payload,
});
